import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  LayoutGrid, Save, Plus, Trash2, RotateCw, Copy, Loader2,
  X, Circle, Square, RectangleHorizontal, TreePine, Palmtree,
  Flower2, Umbrella, DoorOpen, Fence
} from 'lucide-react';
import { toast } from 'sonner';

const ZONES = ['interieur', 'veranda', 'exterieur'];
const ZONE_LABELS = { interieur: 'Interieur', veranda: 'Veranda', exterieur: 'Terrasse' };
const ZONE_COLORS = { interieur: '#C19C7B', veranda: '#81C784', exterieur: '#64B5F6' };

const TABLE_TYPES = [
  { type: 'TABLE_ROUND_2', label: 'Ronde 2p', capacity: 2, icon: Circle, w: 60, h: 60 },
  { type: 'TABLE_ROUND_4', label: 'Ronde 4p', capacity: 4, icon: Circle, w: 70, h: 70 },
  { type: 'TABLE_ROUND_6', label: 'Ronde 6p', capacity: 6, icon: Circle, w: 85, h: 85 },
  { type: 'TABLE_SQUARE_2', label: 'Carree 2p', capacity: 2, icon: Square, w: 55, h: 55 },
  { type: 'TABLE_SQUARE_4', label: 'Carree 4p', capacity: 4, icon: Square, w: 70, h: 70 },
  { type: 'TABLE_RECT_4', label: 'Rect 4p', capacity: 4, icon: RectangleHorizontal, w: 90, h: 55 },
  { type: 'TABLE_RECT_6', label: 'Rect 6p', capacity: 6, icon: RectangleHorizontal, w: 110, h: 60 },
  { type: 'TABLE_RECT_8', label: 'Rect 8p', capacity: 8, icon: RectangleHorizontal, w: 130, h: 65 },
];

const DECOR_TYPES = [
  { type: 'TREE', label: 'Arbre', icon: TreePine, w: 40, h: 40 },
  { type: 'PALM', label: 'Palmier', icon: Palmtree, w: 40, h: 40 },
  { type: 'PLANT', label: 'Plante', icon: Flower2, w: 30, h: 30 },
  { type: 'UMBRELLA', label: 'Parasol', icon: Umbrella, w: 50, h: 50 },
  { type: 'DOOR', label: 'Porte', icon: DoorOpen, w: 40, h: 20 },
  { type: 'FENCE', label: 'Barriere', icon: Fence, w: 60, h: 10 },
];

const AdminFloorPlan = () => {
  const [plan, setPlan] = useState({ elements: { interieur: [], veranda: [], exterieur: [] }, tableCounter: 0 });
  const [activeZone, setActiveZone] = useState('interieur');
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    api.get('/resa/floor-plan')
      .then(r => setPlan(r.data || plan))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const addElement = (typeConfig) => {
    const isTable = typeConfig.capacity !== undefined;
    const newCounter = isTable ? plan.tableCounter + 1 : plan.tableCounter;
    const el = {
      id: isTable ? `table-${activeZone}-${newCounter}` : `decor-${Date.now()}`,
      type: typeConfig.type,
      name: isTable ? `Table ${newCounter}` : typeConfig.label,
      tableNumber: isTable ? newCounter : undefined,
      capacity: typeConfig.capacity,
      x: 100 + Math.random() * 200,
      y: 100 + Math.random() * 200,
      rotation: 0,
      width: typeConfig.w,
      height: typeConfig.h,
    };
    setPlan(p => ({
      ...p,
      tableCounter: newCounter,
      elements: { ...p.elements, [activeZone]: [...(p.elements[activeZone] || []), el] },
    }));
    setSelectedId(el.id);
  };

  const removeElement = (id) => {
    setPlan(p => ({
      ...p,
      elements: {
        ...p.elements,
        [activeZone]: (p.elements[activeZone] || []).filter(e => e.id !== id),
      },
    }));
    setSelectedId(null);
  };

  const duplicateElement = (id) => {
    const el = (plan.elements[activeZone] || []).find(e => e.id === id);
    if (!el) return;
    const isTable = !!el.capacity;
    const newCounter = isTable ? plan.tableCounter + 1 : plan.tableCounter;
    const dup = {
      ...el,
      id: isTable ? `table-${activeZone}-${newCounter}` : `decor-${Date.now()}`,
      name: isTable ? `Table ${newCounter}` : el.name,
      tableNumber: isTable ? newCounter : undefined,
      x: el.x + 30,
      y: el.y + 30,
    };
    setPlan(p => ({
      ...p,
      tableCounter: newCounter,
      elements: { ...p.elements, [activeZone]: [...(p.elements[activeZone] || []), dup] },
    }));
    setSelectedId(dup.id);
  };

  const rotateElement = (id) => {
    setPlan(p => ({
      ...p,
      elements: {
        ...p.elements,
        [activeZone]: (p.elements[activeZone] || []).map(e =>
          e.id === id ? { ...e, rotation: ((e.rotation || 0) + 45) % 360 } : e
        ),
      },
    }));
  };

  const handleMouseDown = (e, id) => {
    e.stopPropagation();
    setSelectedId(id);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const el = (plan.elements[activeZone] || []).find(el => el.id === id);
    if (!el) return;
    setDragging({ id, startX: e.clientX, startY: e.clientY, origX: el.x, origY: el.y });
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    const dx = e.clientX - dragging.startX;
    const dy = e.clientY - dragging.startY;
    setPlan(p => ({
      ...p,
      elements: {
        ...p.elements,
        [activeZone]: (p.elements[activeZone] || []).map(el =>
          el.id === dragging.id ? { ...el, x: Math.max(0, dragging.origX + dx), y: Math.max(0, dragging.origY + dy) } : el
        ),
      },
    }));
  }, [dragging, activeZone]);

  const handleMouseUp = useCallback(() => setDragging(null), []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post('/resa/floor-plan', plan);
      toast.success(`Plan sauvegarde (${res.data.tables_synced} tables synchronisees)`);
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const selectedEl = selectedId ? (plan.elements[activeZone] || []).find(e => e.id === selectedId) : null;
  const zoneElements = plan.elements[activeZone] || [];
  const totalTables = Object.values(plan.elements).flat().filter(e => e.capacity).length;
  const totalCapacity = Object.values(plan.elements).flat().filter(e => e.capacity).reduce((s, e) => s + e.capacity, 0);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  return (
    <div className="space-y-4" data-testid="floor-plan-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LayoutGrid size={18} className="text-[rgb(var(--lm-gold))]" />
          <h1 className="text-lg font-title text-[rgb(var(--lm-text))]">Plan de salle</h1>
          <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">{totalTables} tables &middot; {totalCapacity} places</span>
        </div>
        <Button onClick={handleSave} disabled={saving} size="sm"
          className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 gap-1" data-testid="save-plan">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Sauvegarder
        </Button>
      </div>

      {/* Zone tabs */}
      <div className="flex gap-1" data-testid="zone-tabs">
        {ZONES.map(z => (
          <button key={z} onClick={() => { setActiveZone(z); setSelectedId(null); }}
            className={`px-4 py-2 text-xs transition-all ${
              activeZone === z
                ? 'text-[rgb(var(--lm-bg))]'
                : 'bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-secondary))] border border-[rgb(var(--lm-border))] hover:border-[rgb(var(--lm-gold)/0.3)]'
            }`}
            style={activeZone === z ? { background: ZONE_COLORS[z] } : {}}
            data-testid={`zone-tab-${z}`}>
            {ZONE_LABELS[z]} ({(plan.elements[z] || []).filter(e => e.capacity).length})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Palette */}
        <div className="space-y-3">
          <div className="lm-card p-3">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-2">Tables</p>
            <div className="grid grid-cols-2 gap-1">
              {TABLE_TYPES.map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.type} onClick={() => addElement(t)}
                    className="flex items-center gap-1.5 p-2 text-[10px] text-[rgb(var(--lm-text-secondary))] hover:bg-[rgb(var(--lm-surface-elevated))] rounded-sm transition-colors"
                    data-testid={`add-${t.type}`}>
                    <Icon size={12} className="text-[rgb(var(--lm-gold))]" /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="lm-card p-3">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-2">Decor</p>
            <div className="grid grid-cols-2 gap-1">
              {DECOR_TYPES.map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.type} onClick={() => addElement(t)}
                    className="flex items-center gap-1.5 p-2 text-[10px] text-[rgb(var(--lm-text-secondary))] hover:bg-[rgb(var(--lm-surface-elevated))] rounded-sm transition-colors">
                    <Icon size={12} className="text-[rgb(var(--lm-text-muted))]" /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected element */}
          {selectedEl && (
            <div className="lm-card p-3 space-y-2" data-testid="element-props">
              <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Element selectionne</p>
              <p className="text-sm text-[rgb(var(--lm-text))] font-medium">{selectedEl.name}</p>
              {selectedEl.capacity && <p className="text-xs text-[rgb(var(--lm-text-muted))]">{selectedEl.capacity} places</p>}
              <div className="flex gap-1 pt-1">
                <button onClick={() => rotateElement(selectedId)} className="p-1.5 hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] rounded-sm" title="Pivoter">
                  <RotateCw size={12} />
                </button>
                <button onClick={() => duplicateElement(selectedId)} className="p-1.5 hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] rounded-sm" title="Dupliquer">
                  <Copy size={12} />
                </button>
                <button onClick={() => removeElement(selectedId)} className="p-1.5 hover:bg-red-500/10 text-[rgb(var(--lm-red))] rounded-sm" title="Supprimer">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Canvas */}
        <div className="lg:col-span-3">
          <div ref={canvasRef}
            onClick={() => setSelectedId(null)}
            className="lm-card relative overflow-hidden"
            style={{ height: 500, cursor: dragging ? 'grabbing' : 'default' }}
            data-testid="floor-canvas">
            {/* Grid bg */}
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle, rgb(var(--lm-border)) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              opacity: 0.3,
            }} />

            {zoneElements.map(el => {
              const isTable = !!el.capacity;
              const isSelected = el.id === selectedId;
              const isRound = el.type.includes('ROUND');

              return (
                <div key={el.id}
                  onMouseDown={(e) => handleMouseDown(e, el.id)}
                  className={`absolute flex flex-col items-center justify-center cursor-grab select-none transition-shadow ${
                    isSelected ? 'ring-2 ring-[rgb(var(--lm-gold))] z-10' : 'hover:ring-1 hover:ring-[rgb(var(--lm-gold)/0.3)]'
                  }`}
                  style={{
                    left: el.x,
                    top: el.y,
                    width: el.width,
                    height: el.height,
                    transform: `rotate(${el.rotation || 0}deg)`,
                    background: isTable ? ZONE_COLORS[activeZone] + '25' : 'rgb(var(--lm-surface-elevated))',
                    border: `1.5px solid ${isTable ? ZONE_COLORS[activeZone] : 'rgb(var(--lm-border))'}`,
                    borderRadius: isRound ? '50%' : '4px',
                  }}
                  data-testid={`element-${el.id}`}>
                  <span className="text-[8px] font-mono font-bold" style={{ color: isTable ? ZONE_COLORS[activeZone] : 'rgb(var(--lm-text-muted))' }}>
                    {isTable ? el.tableNumber : ''}
                  </span>
                  {isTable && <span className="text-[7px] text-[rgb(var(--lm-text-muted))]">{el.capacity}p</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFloorPlan;
